// src/pages/en/MainPage_tw.js
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import SupportPopup from "../../components/SupportPopup";
import SupportSection from "../../components/SupportSection";
import "../MainPage.css";

function MainPage_tw() {
  const location = useLocation();
  const pathname = location?.pathname || "/";
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const {
    currentLocale,
    prefix,
    canonicalUrl,
    htmlLang,
    ogLocale,
    inLanguage,
    altPaths,
  } = useMemo(() => {
    const firstSeg = pathname.split("/").filter(Boolean)[0];
    const supported = ["en", "ja", "tw"];
    const locale = supported.includes(firstSeg) ? firstSeg : "ko";

    const pref = locale === "ko" ? "" : `/${locale}`;

    const segments = pathname.split("/").filter(Boolean);
    const suffixSegments = supported.includes(segments[0])
      ? segments.slice(1)
      : segments;
    const suffixPath = "/" + suffixSegments.join("/");
    const normalizedSuffix = suffixPath === "//" ? "/" : suffixPath;

    const alt = {
      ko: normalizedSuffix,
      en: "/en" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      ja: "/ja" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
      tw: "/tw" + (normalizedSuffix === "/" ? "" : normalizedSuffix),
    };

    const htmlLangMap = { ko: "ko", en: "en", ja: "ja", tw: "zh-TW" };
    const ogLocaleMap = { ko: "ko_KR", en: "en_US", ja: "ja_JP", tw: "zh_TW" };
    const inLangMap = { ko: "ko-KR", en: "en", ja: "ja", tw: "zh-TW" };

    return {
      currentLocale: locale,
      prefix: pref,
      canonicalUrl: (origin || "") + pathname,
      htmlLang: htmlLangMap[locale],
      ogLocale: ogLocaleMap[locale],
      inLanguage: inLangMap[locale],
      altPaths: alt,
    };
  }, [pathname, origin]);

  const jsonLd = useMemo(() => {
    const obj = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "TooLoves",
      description: "快速在一個地方使用常用工具",
      inLanguage,
      potentialAction: {
        "@type": "SearchAction",
        target: `${origin}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    };
    if (canonicalUrl) obj.url = canonicalUrl;
    return JSON.stringify(obj);
  }, [canonicalUrl, inLanguage, origin]);

  const alternateLinks =
    origin &&
    [
      <link key="ko" rel="alternate" hrefLang="ko" href={origin + altPaths.ko} />,
      <link key="en" rel="alternate" hrefLang="en" href={origin + altPaths.en} />,
      <link key="ja" rel="alternate" hrefLang="ja" href={origin + altPaths.ja} />,
      <link key="tw" rel="alternate" hrefLang="zh-TW" href={origin + altPaths.tw} />,
      <link key="xdef" rel="alternate" hrefLang="x-default" href={origin + altPaths.ko} />,
    ];

  return (
    <div className="main-wrap">
      <SupportPopup locale="tw" />
      <Helmet htmlAttributes={{ lang: htmlLang }}>
        <title>TooLoves</title>
        <meta name="description" content="快速在一個地方使用常用工具" />
        <meta
          name="keywords"
          content="TooLoves, 工具集,QR, QR碼產生器, Base64,浮水印,浮水印產生, EXIF, 中繼資料擷取, AI 圖片, 免費, 網頁工具,部落格,網頁開發"
        />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <meta name="application-name" content="TooLoves" />
        <meta name="format-detection" content="telephone=no" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#ffffff" />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content={ogLocale} />
        <meta property="og:site_name" content="TooLoves" />
        <meta property="og:title" content="TooLoves" />
        <meta
          property="og:description"
          content="快速在一個地方使用常用工具"
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:image" content="/og-main.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TooLoves" />
        <meta
          name="twitter:description"
          content="快速在一個地方使用常用工具"
        />
        <meta name="twitter:image" content="/og-main.png" />

        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
        {alternateLinks}

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd }}
        />

        <style>
          {`
            .lang-switcher {
              display: flex;
              align-items: center;
              gap: 8px;
              font-size: 14px;
            }
            .lang-switcher .lang-btn {
              color: inherit;
              opacity: 0.7;
              text-decoration: none;
              padding: 4px 8px;
              border-radius: 8px;
              transition: opacity .15s, background-color .15s;
            }
            .lang-switcher .lang-btn:hover {
              opacity: 1;
              background: rgba(0,0,0,0.06);
            }
            .lang-switcher .lang-btn.active {
              opacity: 1;
              font-weight: 600;
              background: rgba(0,0,0,0.08);
            }
            .lang-switcher .lang-sep {
              opacity: 0.4;
              user-select: none;
            }
            @media (prefers-color-scheme: dark) {
              .lang-switcher .lang-btn:hover {
                background: rgba(255,255,255,0.08);
              }
              .lang-switcher .lang-btn.active {
                background: rgba(255,255,255,0.12);
              }
            }
          `}
        </style>
      </Helmet>

      <header className="main-hero">
        <nav className="main-nav">
          <div className="nav-left"></div>
          <div className="nav-right">
            <div className="lang-switcher" role="group" aria-label="語言選擇">
              {["ko", "en", "ja", "tw"].map((lang, i, arr) => (
                <React.Fragment key={lang}>
                  <Link
                    to={altPaths[lang]}
                    className={`lang-btn ${
                      currentLocale === lang ? "active" : ""
                    }`}
                    lang={lang === "tw" ? "zh-TW" : lang}
                    aria-current={
                      currentLocale === lang ? "page" : undefined
                    }
                  >
                    {lang === "ko"
                      ? "한국어"
                      : lang === "en"
                      ? "English"
                      : lang === "ja"
                      ? "日本語"
                      : "繁體中文"}
                  </Link>
                  {i < arr.length - 1 && (
                    <span className="lang-sep" aria-hidden="true">
                      /
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </nav>

        <div className="hero-content">
          <h1 className="hero-title">TooLoves</h1>
          <p className="hero-subtitle">快速在一個地方使用常用工具</p>
        </div>
      </header>

      <main className="services">
        <SupportSection locale="tw" />
        <h2 className="section-title">服務總覽</h2>
        <div className="service-grid">
          {/* QR碼產生器 */}
          <article className="service-card">
            <div className="service-icon service-icon-qr" aria-hidden="true">
              <svg viewBox="0 0 64 64" width="48" height="48" role="img">
                <rect x="6" y="6" width="18" height="18" rx="2" />
                <rect x="40" y="6" width="18" height="18" rx="2" />
                <rect x="6" y="40" width="18" height="18" rx="2" />
                <rect x="26" y="26" width="12" height="12" />
              </svg>
            </div>
            <h3 className="service-name">QR 碼</h3>
            <p className="service-desc">將網址轉為 QR 並保存為圖片</p>
            <Link to={`${prefix}/qr`} className="btn-primary">
              前往 QR 產生器
            </Link>
          </article>

          {/* 圖片浮水印 */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🖼️</div>
            <h3 className="service-name">新增圖片浮水印</h3>
            <p className="service-desc">在圖片中建立專屬浮水印</p>
            <Link to={`${prefix}/watermark`} className="btn-primary">
              前往浮水印產生器
            </Link>
          </article>

          {/* AI 圖片 EXIF */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🌀</div>
            <h3 className="service-name">NovelAI 圖片 EXIF 擷取器</h3>
            <p className="service-desc">輕鬆查看 NovelAI 的中繼資料</p>
            <Link to={`${prefix}/aiexif`} className="btn-primary">
              前往 EXIF 擷取器
            </Link>
          </article>

          {/* Base64 編碼/解碼 */}
          <article className="service-card">
            <div className="service-icon" aria-hidden="true">🔤</div>
            <h3 className="service-name">Base64 編碼器 / 解碼器</h3>
            <p className="service-desc">將文字轉為 Base64 或反向轉換</p>
            <Link to={`${prefix}/base64`} className="btn-primary">
              前往 Base64 轉換器
            </Link>
          </article>
        </div>
      </main>

      <footer className="main-footer">
        <span>© 2025 TooLoves. 版權所有。</span>
        <nav className="footer-links" aria-label="法律聲明連結">
          <Link to={`${prefix}/terms`} className="footer-link">
            服務條款
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <Link to={`${prefix}/privacy`} className="footer-link">
            隱私權政策
          </Link>
          <span className="footer-sep" aria-hidden="true">·</span>
          <a
            href="https://tooloves.net/blog"
            className="footer-link"
            target="_blank"
            rel="noopener noreferrer"
          >
            部落格
          </a>
        </nav>
      </footer>
    </div>
  );
}

export default MainPage_tw;
